/**
 * KML Parser - pure regex parsing without external dependencies
 * Max file size capped at 10MB at the infrastructure level
 */
import { DOMParser } from '@xmldom/xmldom';

type XmlDocument = ReturnType<DOMParser['parseFromString']>;
type XmlElement = ReturnType<XmlDocument['getElementsByTagName']>[number];

export interface KmlCoordinate {
    lat: string;
    lng: string;
}

export interface KmlLineStringCoordinate {
    lat: number;
    lng: number;
}

export interface KmlExtremities {
    name: string;
    first: KmlCoordinate;
    last: KmlCoordinate;
}

interface KmlParserOptions {
    /** Maximum number of placemarks to process (default: 1000) */
    maxPlacemarks?: number;
    /** Maximum coordinates per placemark before aborting (default: 10000) */
    maxCoordinatesPerPlacemark?: number;
}

const DEFAULT_OPTIONS: Required<KmlParserOptions> = {
    maxPlacemarks: 1000,
    maxCoordinatesPerPlacemark: 10000,
};

/**
 * Parses a KML string into a DOM Document.
 */
function parseKmlDocument(kmlString: string): XmlDocument {
    // Suppress xmldom warnings/errors so malformed attributes (e.g. duplicate ids)
    // don't abort parsing before we reach the LineString at the end of the document.
    const noop = () => undefined;
    return new DOMParser({ errorHandler: noop }).parseFromString(kmlString, 'text/xml');
}

/**
 * Extracts all elements by local name across any namespace.
 * Handles xmlns variants like plain "kml", "gx", etc.
 */
function getElementsByLocalName(doc: XmlDocument | XmlElement, localName: string): XmlElement[] {
    const results: XmlElement[] = [];
    const all = doc.getElementsByTagName('*');
    for (let i = 0; i < all.length; i++) {
        const el = all[i] as XmlElement;
        if (el.localName === localName) results.push(el);
    }
    return results;
}

/**
 * Parses a KML <coordinates> text content.
 * Handles "lng,lat[,alt] lng,lat[,alt]" and all-comma-separated formats.
 */
function parseCoordinatesText(text: string): KmlLineStringCoordinate[] {
    const results: KmlLineStringCoordinate[] = [];
    const whitespacePoints = text.trim().split(/\s+/);

    for (const raw of whitespacePoints) {
        const parts = raw.split(',');
        if (parts.length < 2) continue;
        const lng = parseFloat(parts[0]);
        const lat = parseFloat(parts[1]);
        if (!isNaN(lat) && !isNaN(lng)) results.push({ lat, lng });
    }

    if (results.length > 1) return results;

    // Fallback: all-comma-separated (lng,lat,alt,lng,lat,alt,...)
    // Clear any partial result from the whitespace pass before reparsing.
    results.length = 0;
    const parts = text
        .trim()
        .split(',')
        .map((s) => parseFloat(s.trim()));
    for (let i = 0; i + 1 < parts.length; i += 3) {
        const lng = parts[i];
        const lat = parts[i + 1];
        if (!isNaN(lat) && !isNaN(lng)) results.push({ lat, lng });
    }

    return results;
}

/**
 * Parses KML coordinate text (format: lng,lat[,alt] separated by whitespace)
 * Returns only the first and last coordinate to avoid unnecessary allocations
 */
function parseFirstAndLastCoordinate(
    coordsText: string,
    maxCoords: number,
): { first: KmlCoordinate; last: KmlCoordinate } | null {
    const points = coordsText.trim().split(/\s+/);

    if (points.length > maxCoords) {
        throw new Error(`Placemark exceeds max coordinates limit: ${maxCoords}.`);
    }

    let first: KmlCoordinate | null = null;
    let last: KmlCoordinate | null = null;

    for (let i = 0; i < points.length; i++) {
        const parts = points[i].split(',');
        if (parts.length < 2) continue;

        const lng = parts[0];
        const lat = parts[1];

        if (isNaN(parseFloat(lat)) || isNaN(parseFloat(lng))) continue;

        const coord: KmlCoordinate = { lat, lng };
        if (!first) first = coord;
        last = coord;
    }

    if (!first || !last) return null;
    return { first, last };
}

/**
 * Extracts content between an XML open/close tag pair (first occurrence)
 * Handles optional attributes on the opening tag
 */
function extractTagContent(xml: string, tagName: string): string {
    const openIdx = xml.search(new RegExp(`<${tagName}[\\s>]`, 'i'));
    if (openIdx === -1) return '';

    const closeAfterOpen = xml.indexOf('>', openIdx);
    if (closeAfterOpen === -1) return '';

    const closeTag = `</${tagName}>`;
    const closeIdx = xml.toLowerCase().indexOf(closeTag.toLowerCase(), closeAfterOpen + 1);
    if (closeIdx === -1) return '';

    return xml.substring(closeAfterOpen + 1, closeIdx).trim();
}

/**
 * Extracts coordinate text from a Placemark's geometry element
 * Supports Point, LineString, and Polygon (outer boundary)
 */
function extractCoordinatesText(placemark: string): string {
    // Try Point
    const point = extractTagContent(placemark, 'Point');
    if (point) {
        const coords = extractTagContent(point, 'coordinates');
        if (coords) return coords;
    }

    // Try LineString
    const lineString = extractTagContent(placemark, 'LineString');
    if (lineString) {
        const coords = extractTagContent(lineString, 'coordinates');
        if (coords) return coords;
    }

    // Try Polygon outer boundary
    const polygon = extractTagContent(placemark, 'Polygon');
    if (polygon) {
        const outer = extractTagContent(polygon, 'outerBoundaryIs');
        if (outer) {
            const ring = extractTagContent(outer, 'LinearRing');
            if (ring) return extractTagContent(ring, 'coordinates');
        }
    }

    // Fallback: bare <coordinates> directly in placemark
    return extractTagContent(placemark, 'coordinates');
}

/**
 * Extracts the first and last coordinate from the full set of Placemarks in a KML string.
 * Returns the first coordinate of the first Placemark and the last coordinate of the last Placemark.
 *
 * @param kmlString - KML file content (max 10MB enforced upstream)
 * @param opts - Optional limits
 */
export function extractKmlExtremities(kmlString: string, opts: KmlParserOptions = {}): KmlExtremities[] {
    const options = { ...DEFAULT_OPTIONS, ...opts };

    // Quick count to reject files with excessive placemarks
    const placemarksCount = (kmlString.match(/<Placemark[\s>]/gi) || []).length;
    if (placemarksCount > options.maxPlacemarks) {
        throw new Error(`KML contains too many placemarks: ${placemarksCount} (max: ${options.maxPlacemarks}).`);
    }

    const results: KmlExtremities[] = [];
    const placemarksRegex = /<Placemark[\s>][\s\S]*?<\/Placemark>/gi;
    let match: RegExpExecArray | null;

    while ((match = placemarksRegex.exec(kmlString)) !== null) {
        const placemarkXml = match[0];
        const name = extractTagContent(placemarkXml, 'name');
        const coordsText = extractCoordinatesText(placemarkXml);

        if (!coordsText) continue;

        const extremes = parseFirstAndLastCoordinate(coordsText, options.maxCoordinatesPerPlacemark);
        if (extremes) {
            results.push({ name, ...extremes });
        }
    }

    return results;
}

/**
 * Extracts all route coordinates from a KML string using @mapbox/togeojson.
 *
 * Handles all KML geometry variants:
 * - <LineString><coordinates> — standard polyline
 * - <MultiGeometry> containing multiple LineStrings
 * - <gx:Track><gx:coord> — Google Earth track export
 *
 * Returns coordinates as {lat, lng} in order.
 */
export function extractKmlLineStringCoordinates(kmlString: string): KmlLineStringCoordinate[] {
    const doc = parseKmlDocument(kmlString);

    // 1. Try <LineString> (standard KML — matches the established pattern)
    const lineStrings = getElementsByLocalName(doc, 'LineString');
    if (lineStrings.length > 0) {
        const coords: KmlLineStringCoordinate[] = [];
        for (const ls of lineStrings) {
            const coordEls = getElementsByLocalName(ls, 'coordinates');
            if (coordEls.length > 0) {
                coords.push(...parseCoordinatesText(coordEls[0].textContent || ''));
            }
        }
        if (coords.length > 0) return coords;
    }

    // 2. Try <gx:Track> and <gx:MultiTrack> (Google Earth track exports)
    const gxTracks = getElementsByLocalName(doc, 'Track');
    if (gxTracks.length > 0) {
        const coords: KmlLineStringCoordinate[] = [];
        for (const track of gxTracks) {
            const coordEls = getElementsByLocalName(track, 'coord');
            for (let i = 0; i < coordEls.length; i++) {
                const text = (coordEls[i].textContent || '').trim();
                const parts = text.split(/\s+/);
                if (parts.length < 2) continue;
                const lng = parseFloat(parts[0]);
                const lat = parseFloat(parts[1]);
                if (!isNaN(lat) && !isNaN(lng)) coords.push({ lat, lng });
            }
        }
        if (coords.length > 0) return coords;
    }

    return [];
}

/**
 * Extracts the href URL from the first <NetworkLink> element in a KML string.
 * Returns null if the KML has no NetworkLink.
 */
export function extractNetworkLinkHref(kmlString: string): string | null {
    const doc = parseKmlDocument(kmlString);
    const links = getElementsByLocalName(doc, 'NetworkLink');
    if (links.length === 0) return null;

    const hrefs = getElementsByLocalName(links[0], 'href');
    if (hrefs.length === 0) return null;

    const href = hrefs[0].textContent?.trim() ?? '';
    return href || null;
}

// RFC-1918 + loopback + link-local ranges blocked to prevent SSRF
const BLOCKED_RANGES: Array<{ prefix: string; bits: number }> = [
    { prefix: '10.', bits: 8 },
    { prefix: '172.', bits: 12 }, // 172.16.0.0/12
    { prefix: '192.168.', bits: 16 },
    { prefix: '127.', bits: 8 },
    { prefix: '169.254.', bits: 16 }, // link-local / cloud metadata
    { prefix: '0.', bits: 8 },
    { prefix: '::1', bits: 128 }, // IPv6 loopback
    { prefix: 'fc', bits: 7 }, // IPv6 ULA
    { prefix: 'fe80', bits: 10 }, // IPv6 link-local
];

/**
 * Validates that a NetworkLink href is safe to fetch.
 * Throws if the URL fails any security check (SSRF prevention).
 */
export function assertNetworkLinkUrlSafe(rawUrl: string): void {
    let parsed: URL;
    try {
        parsed = new URL(rawUrl);
    } catch {
        throw new Error('NetworkLink href is not a valid URL');
    }

    if (parsed.protocol !== 'https:') {
        throw new Error('NetworkLink href must use HTTPS');
    }

    const hostname = parsed.hostname.toLowerCase();

    // Block bare IP addresses
    const isIpv4 = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
    const isIpv6 = hostname.startsWith('[');
    if (isIpv4 || isIpv6) {
        throw new Error('NetworkLink href must not point to an IP address');
    }

    // Block localhost and internal hostnames
    if (hostname === 'localhost' || hostname.endsWith('.local') || hostname.endsWith('.internal')) {
        throw new Error('NetworkLink href points to a disallowed internal host');
    }

    // Block anything that resolves to a known private prefix after stripping brackets
    const bare = hostname.replace(/^\[|\]$/g, '');
    for (const range of BLOCKED_RANGES) {
        if (bare.startsWith(range.prefix)) {
            throw new Error('NetworkLink href points to a disallowed network range');
        }
    }
}

/**
 * Samples waypoints from a coordinate array using uniform distribution.
 * Always includes the first and last points.
 * @param coords Full coordinate array
 * @param maxWaypoints Maximum number of intermediate waypoints (default: 50)
 * @returns { origin, destination, waypoints }
 */
export function sampleRouteWaypoints(
    coords: KmlLineStringCoordinate[],
    maxWaypoints = 50,
): {
    origin: KmlLineStringCoordinate;
    destination: KmlLineStringCoordinate;
    waypoints: KmlLineStringCoordinate[];
} | null {
    if (coords.length < 2) return null;

    const origin = coords[0];
    const destination = coords[coords.length - 1];

    if (coords.length <= maxWaypoints + 2) {
        return { origin, destination, waypoints: coords.slice(1, -1) };
    }

    const inner = coords.slice(1, -1);
    const step = inner.length / maxWaypoints;
    const waypoints: KmlLineStringCoordinate[] = [];

    for (let i = 0; i < maxWaypoints; i++) {
        const idx = Math.min(Math.round(i * step), inner.length - 1);
        waypoints.push(inner[idx]);
    }

    return { origin, destination, waypoints };
}
